/**
 * earcons — sons courts de transition (US-VDPI-03 AC3).
 *
 * Web Audio API native, aucune dépendance ni asset externe :
 *   - `saisi`   : bip montant court à l'ouverture (détection wake "saisi").
 *   - `success` : bip montant clair à l'écriture confirmée (commit OK).
 *   - `error`   : bip grave distinct à l'échec (3ᵉ son, plan §1.3 S5).
 *   - `stop`    : bip descendant court à la clôture / annulation.
 *
 * KISS : un seul AudioContext partagé, créé à la première lecture
 * (geste utilisateur déjà acquis via le micro).
 */

export type EarconSound = 'saisi' | 'success' | 'error' | 'stop';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null;
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

/** Deux notes courtes ; sens (montant/descendant) selon le son. */
const TONES: Record<EarconSound, [number, number]> = {
  saisi: [660, 990],    // montant : ouverture
  success: [784, 1175], // montant clair (G5→D6) : écriture confirmée
  error: [311, 233],    // grave descendant : échec (distinct)
  stop: [880, 440],     // descendant : clôture / annulation
};

export function playEarcon(sound: EarconSound): void {
  const audio = getCtx();
  if (!audio) return;
  if (audio.state === 'suspended') void audio.resume();

  const [f1, f2] = TONES[sound];
  const now = audio.currentTime;
  const step = 0.09;

  [f1, f2].forEach((freq, i) => {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const t0 = now + i * step;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + step);
    osc.connect(gain).connect(audio.destination);
    osc.start(t0);
    osc.stop(t0 + step);
  });
}
